import { useCallback, useEffect, useRef, useState } from 'react'
import { Brain, ClipboardList, GraduationCap, Play, RotateCcw, Square } from 'lucide-react'
import { Button, EmptyHint, Metric } from '../ui/Panels'
import { StateBadge } from '../ui/State'
import {
  fetchL3Eval,
  fetchL3Status,
  l3Action,
  request,
  json,
  TIMEOUT,
  type L3Eval,
  type L3Status,
} from '../../lib/api'
import { useAuthSession } from '../../lib/ivoAuth'

/* ============================================================================
 * ПАНЕЛЬ L3 — ТРЕНАЖЁР №1 «UTM-ДИСПЕТЧЕР НА TWIN» (2026-10-07i)
 *
 * Решение владельца: тренажёр №1 = стажёр-диспетчер видит SIM-обстановку
 * в ИВО (карта, геозоны, предикт конфликтов), командует; инструктор
 * запускает сценарии и оценивает. Сценарии — ТОЛЬКО на twin-стенде
 * (utm_twin_fb :8010), прод-FB и прод-zone_guard не затрагиваются:
 * сессия живёт в отдельном процессе l3_orchestrator (:8106), консоль
 * только оркестрирует и проксирует.
 *
 * Роли (Keycloak realm utm, 2026-10-07i): l3_instructor управляет
 * сессией (кнопки), l3_trainee видит статус/события без кнопок;
 * ЦУП-диспетчер допущен как суперпользователь (RULES catch-all).
 *
 * Кнопки:
 *   • Старт сценария 1/2 — чистый старт twin (DELETE journal+obs,
 *     FLUSHDB redis, zg_reset) + инжект + заявки + мост BlueSky(:8100)→twin
 *   • Сброс — zg_reset_session прод-консоли (ручка 07h, буферы карты)
 *   • Стоп — SIGTERM оркестратору
 *   • AI-эталон — переключить PPO ppo_mergeenv_v3 в тренере (:8100/auto)
 *
 * Переключатель источника карты (src/onSrc): 'prod' — обычный трафик
 * прод-FB; 'l3' — SIM-обстановка twin-сессии (через /api/traffic?source=l3).
 * Прод-zg при 'l3' twin-трафик НЕ получает (изоляция сценария).
 * ========================================================================= */

export type TrafficSource = 'prod' | 'l3'

interface L3PanelProps {
  src: TrafficSource
  onSrc: (s: TrafficSource) => void
}

/** Период опроса статуса оркестратора, мс (тик инжекта 2 с) */
const L3_POLL_MS = 3000

export function L3Panel({ src, onSrc }: L3PanelProps) {
  const [status, setStatus] = useState<L3Status | null>(null)
  const [busy, setBusy] = useState<'' | 'start' | 'stop' | 'ai' | 'reset'>('')
  const [scenario, setScenario] = useState<1 | 2>(2)
  const [lastMsg, setLastMsg] = useState<string | null>(null)
  const [evalData, setEvalData] = useState<L3Eval | null>(null)
  const alive = useRef(true)
  const auth = useAuthSession()
  // Инструктор: l3-роль из Keycloak ИЛИ ЦУП (суперпользователь консоли).
  const isInstructor =
    !!auth.me?.roles &&
    (auth.me.roles.includes('l3_instructor') ||
      auth.me.roles.includes('dispatcher'))

  const poll = useCallback(async (signal?: AbortSignal) => {
    try {
      const s = await fetchL3Status(signal)
      if (alive.current) setStatus(s)
    } catch {
      /* деградация: статус недоступен — показываем последний */
    }
  }, [])

  useEffect(() => {
    alive.current = true
    const ac = new AbortController()
    poll(ac.signal)
    const t = setInterval(() => poll(ac.signal), L3_POLL_MS)
    return () => {
      alive.current = false
      ac.abort()
      clearInterval(t)
    }
  }, [poll])

  // Журнал оценки (2026-10-07j): опрос /api/l3/eval тем же интервалом.
  // Деградация не ломает панель — остаётся последний снимок.
  useEffect(() => {
    const ac = new AbortController()
    const tick = () => {
      fetchL3Eval(ac.signal)
        .then((e) => {
          if (alive.current) setEvalData(e)
        })
        .catch(() => {
          /* оркестратор недоступен — держим последний снимок */
        })
    }
    tick()
    const t = setInterval(tick, L3_POLL_MS)
    return () => {
      ac.abort()
      clearInterval(t)
    }
  }, [])

  const run = async (action: 'start' | 'stop' | 'ai' | 'reset') => {
    setBusy(action)
    setLastMsg(null)
    try {
      // reset (2026-10-07i) — отдельная ручка /api/l3/reset (zg_reset_session
      // буферов прод-карты); start/stop/ai — на оркестратор через l3Action.
      const res = (action === 'reset'
        ? await request<{ ok: boolean; error?: string }>('/api/l3/reset', json({}), TIMEOUT.action)
        : (await l3Action(action, action === 'start' ? scenario : undefined))) as {
        ok?: boolean
        error?: string
        stopped?: string
        auto?: boolean
      }
      if (action === 'start') setLastMsg(res.ok ? 'сессия запущена' : res.error ?? 'старт не удался')
      if (action === 'stop') setLastMsg(res.ok ? 'сессия остановлена' : res.error ?? 'стоп не удался')
      if (action === 'ai') setLastMsg(res.error ? `AI: ${res.error}` : `AI-эталон: ${res.auto ? 'ON' : 'OFF'}`)
      if (action === 'reset') setLastMsg(res.ok ? 'буферы карты сброшены' : res.error ?? 'сброс не удался')
    } catch (e) {
      setLastMsg(`ошибка: ${(e as Error).message}`)
    } finally {
      setBusy('')
      poll()
    }
  }

  const running = status?.running === true

  // Подпись команды стажёра: «hdg+1» / «spd-1»
  const cmdText = (d: Record<string, unknown> | undefined) => {
    if (!d || d.kind == null) return ''
    const k = String(d.kind)
    const dir = typeof d.dir === 'number' ? d.dir : 0
    const sign = dir > 0 ? '+' : ''
    return ` ${k}${sign}${dir !== 0 ? dir : ''}`
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Источник карты: прод-трафик или twin-сессия */}
      <div className="flex flex-wrap items-center gap-2 rounded border border-line px-2 py-1.5">
        <GraduationCap size={16} aria-hidden />
        <span className="text-sm font-medium">Карта:</span>
        {(['prod', 'l3'] as const).map((s) => (
          <button
            key={s}
            onClick={() => onSrc(s)}
            className={
              'rounded px-2 py-0.5 text-sm ' +
              (src === s ? 'bg-[var(--accent,#3b82f6)] text-white' : 'border border-line')
            }
          >
            {s === 'prod' ? 'Прод (FB)' : 'Twin (L3-сценарий)'}
          </button>
        ))}
        <span className="ml-auto text-xs opacity-60">
          {src === 'l3'
            ? 'SIM-обстановка twin; прод-зон guard не затрагивается'
            : 'обычный трафик прод-FB'}
        </span>
      </div>

      {/* Управление сессией — только инструктор (l3_instructor / ЦУП) */}
      {isInstructor && (
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={scenario}
          onChange={(e) => setScenario(Number(e.target.value) === 1 ? 1 : 2)}
          disabled={running}
          className="rounded border border-line bg-transparent px-2 py-1 text-sm"
        >
          <option value={1}>Сценарий 1: свои / чужой</option>
          <option value={2}>Сценарий 2: массовое нарушение</option>
        </select>
        <Button
          onClick={() => run('start')}
          disabled={busy !== '' || running}
          title="Чистый старт twin + инжект + мост BlueSky"
        >
          <Play size={14} /> Старт
        </Button>
        <Button
          onClick={() => run('stop')}
          disabled={busy !== '' || !running}
          title="SIGTERM оркестратору"
        >
          <Square size={14} /> Стоп
        </Button>
        <Button
          onClick={() => run('ai')}
          disabled={busy !== '' || !running}
          title="Переключить AI-эталон PPO в тренере"
        >
          <Brain size={14} /> AI-эталон
        </Button>
        <Button
          onClick={() => run('reset')}
          disabled={busy !== ''}
          title="zg_reset_session прод-консоли (сброс подсветки/кулдаунов)"
        >
          <RotateCcw size={14} /> Сброс карты
        </Button>
        <StateBadge state={running ? 'active' : status?.error ? 'warning' : 'pending'}>
          {running ? 'СЕССИЯ ИДЁТ' : 'НЕТ СЕССИИ'}
        </StateBadge>
      </div>
      )}

      {/* Стажёр: краткая подсказка вместо кнопок */}
      {!isInstructor && (
        <div className="text-xs opacity-70">
          Режим стажёра: управление сценарием у инструктора. Наблюдайте
          обстановку на карте (переключатель «Twin (L3-сценарий)») и события ниже.
        </div>
      )}

      {lastMsg && <div className="text-xs opacity-70">{lastMsg}</div>}

      {/* Статус сессии */}
      {status?.running ? (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <Metric label="Сценарий" value={String(status.scenario ?? '—')} />
          <Metric label="Аптайм, с" value={String(status.uptime_s ?? '—')} />
          <Metric
            label="Инжект"
            value={`${status.inject?.obs ?? 0} obs / ${status.inject?.ticks ?? 0} тик`}
          />
          <Metric
            label="Мост BlueSky"
            value={status.bridge?.paused ? 'пауза' : `${status.bridge?.ac ?? 0} борт.`}
          />
          <Metric label="Twin-zg тиков" value={String(status.zg?.ticks ?? 0)} />
          <Metric label="События twin" value={String(status.events?.length ?? 0)} />
          <Metric
            label="Тренер :8100"
            value={status.trainer?.up ? `${status.trainer?.n_ac ?? 0} борт.` : 'недоступен'}
          />
          <Metric
            label="Ближайший, NM"
            value={status.trainer?.nearest_nm != null ? String(status.trainer.nearest_nm) : '—'}
          />
        </div>
      ) : (
        <EmptyHint>
          Сессия не запущена. Выберите сценарий и нажмите «Старт»: twin будет
          очищен (журнал/наблюдения/redis), поднимется инжект SIM-флота, мост
          BlueSky→twin и twin-zone_guard. Прод-контур не затрагивается.
          {status?.error ? ` Ошибка статуса: ${status.error}` : ''}
        </EmptyHint>
      )}

      {/* Лента событий twin-zone_guard */}
      {status?.events && status.events.length > 0 && (
        <div className="rounded border border-line">
          <div className="border-b border-line px-2 py-1 text-sm font-medium">
            События twin (свежие сверху)
          </div>
          <ul className="max-h-48 overflow-auto text-xs">
            {status.events.map((e, i) => (
              <li key={`${e.ts}-${e.drone}-${i}`} className="border-b border-line/50 px-2 py-1 last:border-0">
                <span className="font-mono opacity-60">{e.ts}</span>{' '}
                <span className="font-medium">{e.kind}</span> {e.drone}
                {e.text ? <span className="opacity-70"> — {e.text}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Журнал оценки (2026-10-07j): действия + метрики vs AI-эталон.
          Каждое число — факт с семантикой на бэкенде (semantics в /api/l3/eval):
          реакция = первая команда стажёра ПОСЛЕ zone_intrusion; Δинтр. —
          счётчик близких сближений тренера (<4NM), не zone_guard-нарушения. */}
      {evalData?.ok && (
        <div className="flex flex-col gap-2 rounded border border-line px-2 py-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <ClipboardList size={16} aria-hidden /> Журнал оценки
            <span className="ml-auto text-xs opacity-60">
              AI-эталон: {evalData.session?.ai_active ? 'ON' : 'OFF'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <Metric
              label="Инциденты twin"
              value={String(evalData.scorecard?.incidents_total ?? 0)}
            />
            <Metric
              label="Команды стажёра"
              value={String(evalData.scorecard?.trainee_cmds ?? 0)}
            />
            <Metric
              label="Реакция, с (ср.)"
              value={
                evalData.scorecard?.zone_intrusion_reaction_s?.mean != null
                  ? String(evalData.scorecard.zone_intrusion_reaction_s.mean)
                  : '—'
              }
            />
            <Metric
              label="AI ON, с (всего)"
              value={String(evalData.scorecard?.ai_on_total_s ?? 0)}
            />
          </div>

          {/* Периоды AI-эталона (закрытые) с метриками */}
          {evalData.ai_periods && evalData.ai_periods.length > 0 && (
            <div>
              <div className="pb-1 text-xs font-medium opacity-80">
                Периоды AI-эталона (свежие сверху)
              </div>
              <ul className="max-h-32 overflow-auto text-xs">
                {evalData.ai_periods.slice(0, 6).map((p, i) => (
                  <li key={`${p.from_ts}-${i}`} className="border-b border-line/50 px-0 py-1 last:border-0">
                    <span className={'font-mono ' + (p.on ? 'text-emerald-500' : 'opacity-60')}>
                      {p.on ? 'ON ' : 'OFF'}
                    </span>{' '}
                    <span className="font-mono opacity-60">
                      {p.from_ts.slice(11, 19)}Z→{p.to_ts.slice(11, 19)}Z
                    </span>{' '}
                    {p.dur_s}с · Δближ.сближ.: {p.d_intrusions ?? '—'} · ср.дист.:{' '}
                    {p.mean_nearest_nm != null ? `${p.mean_nearest_nm} NM` : '—'}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Действия стажёра/инструктора */}
          {evalData.trainee_actions && evalData.trainee_actions.length > 0 && (
            <div>
              <div className="pb-1 text-xs font-medium opacity-80">
                Действия (свежие сверху)
              </div>
              <ul className="max-h-40 overflow-auto text-xs">
                {evalData.trainee_actions.slice(0, 10).map((a, i) => (
                  <li key={`${a.ts}-${i}`} className="border-b border-line/50 px-0 py-1 last:border-0">
                    <span className="font-mono opacity-60">{a.ts}</span>{' '}
                    <span className="font-medium">{a.user}</span>{' '}
                    {a.action}
                    {a.action === 'trainee_cmd' ? cmdText(a.detail) : null}
                    {a.action === 'start' && a.detail?.scenario != null
                      ? ` (сц.${String(a.detail.scenario)})`
                      : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}